import { useRef, useState, type CSSProperties } from "react";
import { Link } from "wouter";
import { ArrowUpRight } from "lucide-react";
import { nw, type InputType } from "@/components/landing/landing-data";
import { useOnScreen, usePageVisible, usePointerParallax } from "@/components/landing/landing-motion";

/** Fixed star field (seeded, so it renders the same every time). */
const STARS = (() => {
  const out: { x: number; y: number; size: number; opacity: number; delay: number }[] = [];
  let seed = 13;
  const rand = () => {
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280;
  };
  for (let i = 0; i < 60; i++) {
    out.push({ x: rand() * 100, y: rand() * 100, size: rand() > 0.8 ? 3 : 2, opacity: 0.25 + rand() * 0.6, delay: rand() * 5 });
  }
  return out;
})();

const starStyle = (s: (typeof STARS)[number]): CSSProperties => ({
  left: `${s.x}%`,
  top: `${s.y}%`,
  width: s.size,
  height: s.size,
  opacity: s.opacity,
  animationDelay: `${s.delay.toFixed(2)}s`,
});

/**
 * "What will you unfold today?" Motion (V6): the image drifts and breathes over 24s,
 * zooms gently with scroll on wide screens, the glow pulses every 8s and stars twinkle;
 * a fine pointer adds a few pixels of depth. Everything pauses when off screen.
 */
export function FinalCtaSection({ onUnfold }: { onUnfold: (value: string, type: InputType) => void }) {
  const [value, setValue] = useState("");
  const sectionRef = useRef<HTMLElement>(null);
  const onScreen = useOnScreen(sectionRef, 0.05);
  const pageVisible = usePageVisible();
  usePointerParallax(sectionRef);

  return (
    <section
      ref={sectionRef}
      aria-labelledby="lp-cta-title"
      data-paused={!onScreen || !pageVisible}
      className="relative px-3 pb-10 pt-5 sm:px-8"
    >
      <div className="lp-night lp-expand lp-on-dark mx-auto flex min-h-[540px] max-w-[1376px] items-center justify-center md:min-h-[720px]">
        <div aria-hidden className="lp-ctadrift absolute inset-0">
          <div className="lp-layer absolute inset-0" style={{ "--d": -10 } as CSSProperties}>
            <img
              src="/images/landing/v6/hands-galaxy.webp"
              srcSet="/images/landing/v6/hands-galaxy-720.webp 720w, /images/landing/v6/hands-galaxy.webp 1122w"
              sizes="(min-width: 1376px) 1376px, 100vw"
              alt=""
              width={1122}
              height={1110}
              loading="lazy"
              decoding="async"
              className="lp-zoomsoft absolute inset-0 h-full w-full scale-[1.05] object-cover object-[50%_52%]"
            />
          </div>
        </div>
        <div
          aria-hidden
          className="lp-ctaglow pointer-events-none absolute left-1/2 top-1/2 h-[520px] w-[900px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-60"
          style={{ background: "radial-gradient(ellipse, rgba(184,166,255,.28), transparent 65%)" }}
        />
        <div aria-hidden className="pointer-events-none absolute inset-0 hidden sm:block">
          {STARS.map((s, i) => (
            <span key={i} className="lp-star" style={starStyle(s)} />
          ))}
        </div>
        <div aria-hidden className="pointer-events-none absolute inset-0 sm:hidden">
          {STARS.slice(0, 24).map((s, i) => (
            <span key={i} className="lp-star" style={starStyle(s)} />
          ))}
        </div>
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(ellipse 48% 42% at 50% 50%, rgba(10,9,34,.62), rgba(10,9,34,.18) 75%)" }}
        />

        <div className="relative z-[2] flex w-full max-w-[740px] flex-col items-center gap-7 px-4 py-16 text-center sm:px-8">
          <h2
            id="lp-cta-title"
            className="m-0 text-[clamp(2.25rem,7vw,4.75rem)] font-semibold leading-none tracking-[-0.045em] text-[#F6F4FF]"
          >
            What will you unfold&nbsp;today?
          </h2>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              onUnfold(value, "link");
            }}
            className="lp-cta-box flex w-full flex-col items-stretch gap-2 p-2 text-left sm:flex-row sm:items-center sm:gap-3 sm:py-2.5 sm:pl-6 sm:pr-2.5"
          >
            <label htmlFor="lp-cta-input" className="sr-only">
              Paste a link or share a thought
            </label>
            <input
              id="lp-cta-input"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Paste a link, or share a thought…"
              className="lp-cta-input min-h-[52px] min-w-0 flex-1 bg-transparent px-2.5 text-[15px] text-(--lp-ink) outline-none sm:px-0 sm:text-lg"
            />
            <button type="submit" disabled={!value.trim()} className="lp-cta-go min-h-[52px] w-full text-base font-medium sm:h-[52px] sm:w-[52px]" aria-label="Begin reflection session">
              <span className="sm:hidden">{nw("Begin reflection session")}</span>
              <ArrowUpRight className="h-[22px] w-[22px]" strokeWidth={2.2} aria-hidden />
            </button>
          </form>

          <p className="m-0 text-base text-[#D2CEEA]">
            Your first Unfolding is free.{" "}
            <Link href="/pricing" className="whitespace-nowrap rounded text-white underline underline-offset-4">
              See plans
            </Link>
          </p>
        </div>
      </div>
    </section>
  );
}
